import React, { Component, ErrorInfo, ReactNode } from 'react';
import { reportError } from '@/instrument';
// Polaris imports
import {
  Page,
  Card,
  Text,
  Button,
  BlockStack,
  InlineStack,
  Box,
  Icon,
  Banner,
} from '@/lib/polaris';
import { AlertTriangleIcon, RefreshIcon, HomeIcon, ArrowLeftIcon } from '@/lib/polaris-icons';

interface Props {
  children: ReactNode;
  sectionName: string;
  sectionDescription?: string;
  fallbackRoute?: string;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Error Boundary específico para una sección de la aplicación
 * Permite aislar errores por sección (Super Admin, POS, E-commerce, etc.)
 */
class ErrorBoundarySectionClass extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error(`❌ [ErrorBoundary-${this.props.sectionName}] Error capturado:`, error);
    console.error(`❌ [ErrorBoundary-${this.props.sectionName}] Error Info:`, errorInfo);

    this.setState({
      error,
      errorInfo,
    });

    // H1 I4: reportar a Sentry (no-op si VITE_SENTRY_DSN no está configurado).
    reportError(error, {
      componentStack: errorInfo.componentStack ?? undefined,
      extra: { sectionName: this.props.sectionName },
    });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      // Si hay un fallback personalizado, usarlo
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // UI por defecto de error para la sección
      return (
        <Page>
          <Box padding="800">
            <InlineStack align="center">
              <Card>
                <BlockStack gap="500">
                  <InlineStack gap="300" align="center">
                    <Icon source={AlertTriangleIcon} tone="critical" />
                    <BlockStack gap="050">
                      <Text variant="headingLg" fontWeight="semibold" as="h1">
                        Error en {this.props.sectionName}
                      </Text>
                      <Text variant="bodyMd" tone="subdued" as="p">
                        {this.props.sectionDescription || 
                          `Se produjo un error inesperado en la sección de ${this.props.sectionName}. 
                          El resto de la aplicación continúa funcionando normalmente.`}
                      </Text>
                    </BlockStack>
                  </InlineStack>

                  {import.meta.env.DEV && this.state.error && (
                    <Banner tone="critical">
                      <BlockStack gap="200">
                        <Text variant="bodyMd" fontWeight="semibold" as="p">
                          {this.state.error.name}: {this.state.error.message}
                        </Text>
                        {this.state.errorInfo && (
                          <details>
                            <summary style={{ cursor: 'pointer', marginBottom: '8px' }}>
                              <Text variant="bodySm" tone="subdued" as="span">
                                Detalles técnicos (solo en desarrollo)
                              </Text>
                            </summary>
                            <Box padding="200" background="bg-surface-secondary">
                              <pre
                                style={{
                                  margin: 0,
                                  whiteSpace: 'pre-wrap',
                                  fontFamily: 'var(--p-font-family-mono, monospace)',
                                  fontSize: '0.75rem',
                                }}
                              >
                                {this.state.errorInfo.componentStack}
                              </pre>
                            </Box>
                          </details>
                        )}
                      </BlockStack>
                    </Banner>
                  )}

                  <InlineStack gap="200">
                    <Button onClick={this.handleReset} variant="primary" icon={RefreshIcon}>
                      Reintentar
                    </Button>
                    {this.props.fallbackRoute && (
                      <Button 
                        onClick={() => window.location.href = this.props.fallbackRoute!} 
                        variant="secondary"
                        icon={ArrowLeftIcon}
                      >
                        Volver
                      </Button>
                    )}
                    <Button onClick={() => window.location.href = '/'} variant="secondary" icon={HomeIcon}>
                      Ir al inicio
                    </Button>
                  </InlineStack>
                </BlockStack>
              </Card>
            </InlineStack>
          </Box>
        </Page>
      );
    }

    return this.props.children;
  }
}

// Wrapper con hook para usar navigate si es necesario
export const ErrorBoundarySection: React.FC<Props> = (props) => {
  return <ErrorBoundarySectionClass {...props} />;
};
